import React, { useState, useRef, useEffect, useCallback } from "react";
import { getPagePrompts } from "../utils/pagePrompts";
import { getKpiData } from "./GlobalData";
import { useNavigate } from "react-router-dom";
import { authenticatedFetch } from "../utils/apiInterceptor";
import MarkdownMessage from "./MarkdownMessage";
import {
  buildTerminalTaraContext,
  isTerminalRoute,
  TERMINAL_PAGE,
} from "../utils/taraTerminalContext";

const PROMPT_ICON_SRC = "/images/fluent_prompt-icon.svg";
const DEFAULT_PROMPTS = [
  {
    text: "How can I improve my visibility on Suspire?",
    icon: PROMPT_ICON_SRC,
  },
  { text: "Would 3SC help my brand stand out more?", icon: PROMPT_ICON_SRC },
  {
    text: "Which Growth Suite activity fits my goal best?",
    icon: PROMPT_ICON_SRC,
  },
  { text: "What should I do with my current credits?", icon: PROMPT_ICON_SRC },
  { text: "What is Suspire for Business?", icon: PROMPT_ICON_SRC },
];

export default function ChatbotComponent({
  prompts: staticPrompts,
  avatarSrc = "/images/tara-avatar.png",
  greeting = "Hey, I'm Tara!",
  tagline = "Always around to guide you",
  placeholder = "Ask me anything...",
  sendIconSrc = "/images/chat-send-icon.png",
  onMessageSend,
  onClose, // New prop for closing the chatbot
  containerStyle = {},
  avatarWrapperStyle = {},
  greetingStyle = {},
  taglineStyle = {},
  promptsStyle = {},
  promptButtonStyle = {},
  inputWrapperStyle = {},
  inputStyle = {},
  sendButtonStyle = {},
  pageContext = null, // OPTIONAL: Pass page data if available
  pageData = null, // OPTIONAL: Pass any data the page has loaded
}) {
  const navigate = useNavigate();
  const [chatInput, setChatInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [prompts, setPrompts] = useState(staticPrompts || DEFAULT_PROMPTS);
  const [currentPromptIndex, setCurrentPromptIndex] = useState(0);
  const [isPromptFading, setIsPromptFading] = useState(false);
  const [isLoadingPrompts, setIsLoadingPrompts] = useState(false);
  const [isLimitReached, setIsLimitReached] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const MAX_INPUT_LENGTH = 2000;
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const [isUserScrolledUp, setIsUserScrolledUp] = useState(false);
  const hasFetchedRef = useRef(false);
  const currentPathRef = useRef(window.location.pathname);

  const handleScroll = () => {
    if (chatContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 20;
      setIsUserScrolledUp(!isNearBottom);
    }
  };

  // Fetch page-specific prompts when route changes
  useEffect(() => {
    const fetchPrompts = async () => {
      const currentPath = window.location.pathname;

      console.log("[ChatbotComponent] Fetching prompts for path:", currentPath);
      setIsLoadingPrompts(true);

      try {
        const pagePrompts = await getPagePrompts(currentPath);
        console.log("[ChatbotComponent] Received prompts:", pagePrompts);
        setPrompts(pagePrompts);
        currentPathRef.current = currentPath;
      } catch (error) {
        console.error("[ChatbotComponent] Error fetching prompts:", error);
        setPrompts(staticPrompts || DEFAULT_PROMPTS);
      } finally {
        setIsLoadingPrompts(false);
      }
    };

    // Fetch prompts immediately on mount
    fetchPrompts();

    // Set up a listener for route changes (using a simple interval check)
    const intervalId = setInterval(() => {
      const newPath = window.location.pathname;
      if (newPath !== currentPathRef.current) {
        console.log(
          "[ChatbotComponent] Path changed from",
          currentPathRef.current,
          "to",
          newPath,
        );
        fetchPrompts();
      }
    }, 500); // Check every 500ms

    return () => clearInterval(intervalId);
  }, [staticPrompts]);

  // Handle prompt rotation animation
  useEffect(() => {
    if (prompts.length <= 1) return;

    let fadeTimeoutId;

    const rotationInterval = setInterval(() => {
      setIsPromptFading(true);

      // Wait for fade-out to finish before switching content
      fadeTimeoutId = setTimeout(() => {
        setCurrentPromptIndex((prev) => (prev + 1) % prompts.length);
        setIsPromptFading(false);
      }, 500);
    }, 4000); // Rotate every 4 seconds

    return () => {
      clearInterval(rotationInterval);
      if (fadeTimeoutId) {
        clearTimeout(fadeTimeoutId);
      }
    };
  }, [prompts.length]);

  const handlePromptClick = (prompt) => {
    setChatInput(prompt.text);
  };

  const scrollToBottom = () => {
    if (messagesEndRef.current && chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    if (messages.length > 0 && !isUserScrolledUp) {
      setTimeout(scrollToBottom, 100);
    }
  }, [messages, isUserScrolledUp]);

  // Fetch chat history from Tara endpoints
  const refreshChatHistory = async () => {
    try {
      const response = await authenticatedFetch(
        process.env.REACT_APP_API_BASE_URL + "/api/tara/chatbot/messages/",
        {
          method: "GET",
        },
      );
      if (!response.ok) {
        console.error(
          "Failed to fetch Tara chat history. Status:",
          response.status,
        );
        return;
      }
      const data = await response.json();
      let formattedMessages = [];
      if (data && Array.isArray(data.sessions)) {
        const sessions = data.sessions || [];
        const msgs = sessions.flatMap((s) =>
          (s.messages || []).map((m) => ({
            id: `${s.id}-${m.id}`,
            text: m.message,
            sender: m.role === "user" ? "user" : "ai",
            timestamp: m.timestamp,
          })),
        );
        formattedMessages = msgs.sort(
          (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
        );
      } else if (Array.isArray(data)) {
        formattedMessages = data.map((item) => ({
          id: item.id,
          text: item.message,
          sender: item.role === "user" ? "user" : "ai",
          timestamp: item.timestamp,
        }));
      }
      setMessages(formattedMessages);
    } catch (error) {
      console.error("Error refreshing Tara chat history:", error);
    }
  };

  useEffect(() => {
    const fetchChatHistory = async () => {
      if (hasFetchedRef.current) return;
      hasFetchedRef.current = true;
      await refreshChatHistory();
    };
    fetchChatHistory();
    return () => {
      hasFetchedRef.current = false;
    };
  }, []);

  const handleChatSubmit = async (e) => {
    e.preventDefault();
    if (chatInput.trim() && !isLimitReached) {
      setErrorMessage('');
      const userPrompt = chatInput.trim();
      // Add user message to UI immediately
      const newMessage = { text: userPrompt, sender: "user", id: Date.now() };
      setMessages((prev) => [...prev, newMessage]);
      setChatInput("");
      setIsTyping(true);
      setIsUserScrolledUp(false);

      try {
        console.log("[Tara] User sent message, detecting current page...");

        // Get current page route
        const currentRoute = window.location.pathname;
        console.log("[Tara] Current route:", currentRoute);

        // Fetch ALL the API data that this page would normally fetch
        const pageApiData = await fetchPageApiData(currentRoute);
        console.log("[Tara] Fetched page API data:", pageApiData);

        // Suspire Terminal: everything its screens have loaded this session
        const terminalContext = isTerminalRoute(currentRoute)
          ? await buildTerminalTaraContext(userPrompt).catch((err) => {
              console.error("[Tara] Could not build Terminal context:", err);
              return null;
            })
          : null;

        // Build complete context
        const contextData = {
          route: currentRoute,
          timestamp: new Date().toISOString(),
          api_data: pageApiData,
          ...(terminalContext ? { terminal: terminalContext } : {}),
          // Include any page data passed as props
          ...(pageData || {}),
          ...(pageContext || {}),
        };

        console.log("[Tara] Complete context to send:", contextData);

        const body = {
          prompt: userPrompt,
          page_context: contextData,
        };

        console.log("[Tara] Sending message with full page context");

        const response = await authenticatedFetch(
          process.env.REACT_APP_API_BASE_URL + "/api/tara/chatbot/ask/",
          {
            method: "POST",
            body: JSON.stringify(body),
          },
        );

        let result = null;
        try {
          result = await response.json();
        } catch (err) {
          console.error("[Tara] Could not parse JSON from response:", err);
        }

        console.log("[Tara] Backend response:", result);

        if (!response.ok) {
          const errorText =
            (result && result.message) ||
            (result && result.error) ||
            `Something went wrong (${response.status})`;

          if (response.status === 429) {
            setIsLimitReached(true);
            setErrorMessage(errorText);
          } else if (response.status === 400) {
            setErrorMessage(errorText);
          } else {
            setMessages((prev) => [
              ...prev,
              {
                text: errorText,
                sender: "system",
                id: Date.now(),
              },
            ]);
            console.error(
              "Failed to send message to Tara. Status:",
              response.status,
            );
          }
          setIsTyping(false);
          return;
        }

        // Extract the assistant reply from the correct field
        const assistantMessage =
          result &&
          result.assistant_response &&
          result.assistant_response.message;

        if (assistantMessage) {
          // Show AI response immediately in the chat
          setMessages((prev) => [
            ...prev,
            {
              text: assistantMessage,
              sender: "ai",
              id: Date.now() + 1,
            },
          ]);
          setIsTyping(false);
        } else {
          // Fallback: response parsed but field missing — show placeholder
          setMessages((prev) => [
            ...prev,
            {
              text: "(No response from Tara)",
              sender: "ai",
              id: Date.now() + 1,
            },
          ]);
          setIsTyping(false);
        }

        // Also refresh chat history in the background for full sync
        refreshChatHistory();
      } catch (error) {
        console.error("Error sending message to Tara:", error);

        const errorText =
          (error.data && error.data.message) ||
          (error.data && error.data.error) ||
          (error.message ? error.message : "Unable to reach Tara right now. Please try again.");

        if (error.status === 429 || errorText.toLowerCase().includes('too many requests')) {
          setIsLimitReached(true);
          setErrorMessage(errorText);
        } else if (error.status === 400) {
          setErrorMessage(errorText);
        } else {
          setMessages((prev) => [
            ...prev,
            {
              text: errorText,
              sender: "system",
              id: Date.now(),
            },
          ]);
        }
        setIsTyping(false);
      }
    }
  };

  const handleRaiseTicketClick = (e) => {
    e.preventDefault();
    if (onClose) {
      onClose();
    }
    navigate("/suspire-helpdesk");
  };

  // Function to fetch all API data based on current page
  const fetchPageApiData = async (pathname) => {
    const apiData = {
      page: pathname,
      data: {},
    };

    try {
      // Suspire Terminal: its data travels in page_context.terminal (see
      // handleChatSubmit). Checked first, as "/suspire-terminal/dashboard" would
      // otherwise match the brand dashboard branch below.
      if (isTerminalRoute(pathname)) {
        apiData.data = { page_type: TERMINAL_PAGE };
      }

      // Marketing Suite page
      else if (pathname.includes("/marketing-suite")) {
        console.log("[Tara] Fetching Marketing Suite API data...");
        const [activities, kpis, claims] = await Promise.all([
          authenticatedFetch(process.env.REACT_APP_API_BASE_URL + "/api/activities/", {
            method: "GET",
          })
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null),
          authenticatedFetch(process.env.REACT_APP_API_BASE_URL + "/api/activities/kpis/", {
            method: "GET",
          })
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null),
          authenticatedFetch(process.env.REACT_APP_API_BASE_URL + "/claims/", {
            method: "GET",
          })
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null),
        ]);

        apiData.data = {
          activities,
          kpis,
          claims,
          page_type: "growth_suite",
        };
      }

      // 3SC Questionnaire page (specific)
      else if (pathname.includes("/sustainability/questionnaire")) {
        console.log("[Tara] Fetching 3SC Questionnaire API data...");
        const [questions, answers, submissionStatus] = await Promise.all([
          authenticatedFetch(
            process.env.REACT_APP_API_BASE_URL + "/api/threesc/questions/",
            { method: "GET" },
          )
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null),
          authenticatedFetch(
            process.env.REACT_APP_API_BASE_URL +
              "/api/threesc/fetch-all-answers/",
            { method: "GET" },
          )
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null),
          authenticatedFetch(
            process.env.REACT_APP_API_BASE_URL +
              "/api/threesc/submission-status/",
            { method: "GET" },
          )
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null),
        ]);

        apiData.data = {
          questionnaire: {
            questions,
            answers,
          },
          submission_status: submissionStatus,
          page_type: "3sc_questionnaire",
        };
      }

      // 3SC Sustainability pages (general)
      else if (pathname.includes("/sustainability")) {
        console.log("[Tara] Fetching 3SC API data...");
        const submissionStatus = await authenticatedFetch(
          process.env.REACT_APP_API_BASE_URL +
            "/api/threesc/submission-status/",
          { method: "GET" },
        )
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null);

        apiData.data = {
          submission_status: submissionStatus,
          page_type: "3sc",
        };
      }

      // Brand Dashboard page
      else if (
        pathname.includes("/brand-dashboard") ||
        pathname.includes("/dashboard")
      ) {
        console.log("[Tara] Fetching Brand Dashboard API data...");
        const kpis = getKpiData();
        console.log("DATA", kpis);
        apiData.data = {
          kpis,
          page_type: "brand_dashboard",
        };
      }

      // Profile page
      else if (pathname.includes("/profile")) {
        console.log("[Tara] Fetching Profile API data...");
        const profile = await authenticatedFetch(
          process.env.REACT_APP_API_BASE_URL + "/api/auth/users/profile/",
          { method: "GET" },
        )
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null);

        apiData.data = {
          profile,
          page_type: "profile",
        };
      }

      // Brands page
      else if (pathname.includes("/brands")) {
        console.log("[Tara] Fetching Brands API data...");
        const brands = await authenticatedFetch(
          process.env.REACT_APP_API_BASE_URL + "/brands/",
          { method: "GET" },
        )
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null);

        apiData.data = {
          brands,
          page_type: "product_listing",
        };
      }

      // Wallet/Subscriptions page
      else if (
        pathname.includes("/wallet") ||
        pathname.includes("/subscriptions") ||
        pathname.includes("/pricing")
      ) {
        console.log("[Tara] Fetching Wallet API data...");
        const wallet = await authenticatedFetch(
          process.env.REACT_APP_API_BASE_URL + "/api/payments/wallet/",
          { method: "GET" },
        )
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null);

        apiData.data = {
          wallet,
          page_type: "subscriptions",
        };
      }

      // Community Forum page
      else if (pathname.includes("/community")) {
        console.log("[Tara] Fetching Community Forum API data...");
        const posts = await authenticatedFetch(
          process.env.REACT_APP_API_BASE_URL + "/forum/posts/",
          { method: "GET" },
        )
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null);

        apiData.data = {
          posts,
          page_type: "community_forum",
        };
      }

      // Internal Messages page
      else if (pathname.includes("/messages")) {
        console.log("[Tara] Fetching Messages API data...");
        const messages = await authenticatedFetch(
          process.env.REACT_APP_API_BASE_URL + "/messages/",
          { method: "GET" },
        )
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null);

        apiData.data = {
          messages,
          page_type: "internal_messages",
        };
      }

      // Default/Other pages
      else {
        console.log("[Tara] No specific API data fetching for this page");
        apiData.data = {
          page_type: "other",
          note: "No specific API data for this page",
        };
      }

      console.log("[Tara] Successfully fetched API data for", pathname);
      return apiData;
    } catch (error) {
      console.error("[Tara] Error fetching page API data:", error);
      return {
        page: pathname,
        data: {
          error: "Failed to fetch page data",
          error_message: error.message,
        },
      };
    }
  };

  const defaultStyles = {
    container: {
      background:
        "linear-gradient(145deg, #1e4a4a 0%, #2d5a5a 50%, #1a3f3f 100%)",
      border: "1px solid rgba(255, 255, 255, 0.15)",
      borderRadius: "32px",
      padding: "20px 24px 24px",
      textAlign: "left",
      color: "#ffffff",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      backdropFilter: "blur(20px)",
      WebkitBackdropFilter: "blur(20px)",
      boxShadow: "0 16px 40px rgba(0, 0, 0, 0.25)",
      position: "relative",
      width: "100%",
      height: "100%",
      boxSizing: "border-box",
      margin: "0 auto",
      transition: "all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)",
      overflow: "hidden",
      ...containerStyle,
    },
    avatarWrapper: {
      width: "72px",
      height: "72px",
      borderRadius: "50%",
      overflow: "hidden",
      backgroundColor: "rgba(255, 255, 255, 0.1)",
      border: "2px solid rgba(255, 255, 255, 0.2)",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
      flexShrink: 0,
      ...avatarWrapperStyle,
    },
    avatar: {
      margin: "auto",
      width: "70%",
      height: "100%",
      objectFit: "cover",
    },
    greeting: {
      fontFamily: "Inter, sans-serif",
      fontWeight: 700,
      fontSize: "20px",
      margin: "0",
      color: "#ffffff",
      textShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
      flexShrink: 0,
      lineHeight: 1.3,
      ...greetingStyle,
    },
    tagline: {
      fontFamily: "Inter, sans-serif",
      fontSize: "14px",
      margin: "4px 0 0 0",
      color: "rgba(255, 255, 255, 0.9)",
      flexShrink: 0,
      userSelect: "none",
      WebkitUserSelect: "none",
      msUserSelect: "none",
      caretColor: "transparent",
      cursor: "default",
      ...taglineStyle,
    },
    prompts: {
      display: "flex",
      flexDirection: "column",
      gap: "12px",
      width: "100%",
      marginBottom: "24px",
      flexShrink: 0,
      minHeight: "56px", // Stabilized height to prevent layout shifts
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      position: "relative",
      ...promptsStyle,
    },
    promptButton: {
      width: "100%",
      minHeight: "44px", // Stables the height of each button
      backgroundColor: "rgba(255, 255, 255, 0.05)",
      border: "1px solid rgba(255, 255, 255, 0.1)",
      borderRadius: "24px",
      padding: "12px 16px",
      color: "rgba(255, 255, 255, 0.5)",
      fontSize: "14px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      cursor: "pointer",
      textAlign: "left",
      transition: "opacity 0.6s ease, transform 0.6s ease, background 0.2s ease",
      backdropFilter: "blur(10px)",
      WebkitBackdropFilter: "blur(10px)",
      opacity: 0.6,
      flexShrink: 0, 
      ...promptButtonStyle,
    },
    inputWrapper: {
      width: "100%",
      backgroundColor: "rgba(255, 255, 255, 0.05)",
      border: "1px solid rgba(255, 255, 255, 0.1)",
      borderRadius: "24px",
      padding: "4px",
      display: "flex",
      alignItems: "center",
      backdropFilter: "blur(10px)",
      WebkitBackdropFilter: "blur(10px)",
      opacity: 0.6,
      flexShrink: 0,
      marginBottom: "20px",
      ...inputWrapperStyle,
    },
    input: {
      flexGrow: 1,
      background: "transparent",
      border: "none",
      color: "rgba(255, 255, 255, 0.5)",
      padding: "12px 16px",
      fontSize: "14px",
      outline: "none",
      cursor: "not-allowed",
      ...inputStyle,
    },
    sendButton: {
      background: "rgba(255, 255, 255, 0.1)",
      border: "none",
      borderRadius: "20px",
      padding: "10px",
      cursor: "not-allowed",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "all 0.2s ease",
      opacity: 0.5,
      ...sendButtonStyle,
    },
    comingSoon: {
      fontSize: "12px",
      color: "rgba(255, 255, 255, 0.7)",
      fontStyle: "italic",
      marginTop: "8px",
      textAlign: "center",
    },
  };

  const hasMessages = messages.length > 0;
  return (
    <div style={defaultStyles.container}>
      <style>
        {`
          .custom-scrollbar::-webkit-scrollbar {
            width: 6px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: transparent;
            margin: 12px 0;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: rgba(255, 255, 255, 0.25);
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: rgba(255, 255, 255, 0.4);
          }
          @keyframes dot-blink {
            0% { opacity: 0.2; transform: scale(0.8); }
            50% { opacity: 1; transform: scale(1.1); }
            100% { opacity: 0.2; transform: scale(0.8); }
          }
          .dot-typing span {
            display: inline-block;
            width: 4px;
            height: 4px;
            border-radius: 50%;
            background-color: #1e4a4a;
            animation: dot-blink 1.4s infinite both;
          }
          .dot-typing span:nth-child(1) { animation-delay: 0s; }
          .dot-typing span:nth-child(2) { animation-delay: 0.2s; }
          .dot-typing span:nth-child(3) { animation-delay: 0.4s; }
          
          .chatbot-input::placeholder {
            color: rgba(255, 255, 255, 0.75) !important;
            font-weight: 500 !important;
            opacity: 1 !important;
          }
          @keyframes systemMsgFadeIn {
            from { opacity: 0; transform: translateY(6px); }
            to { opacity: 1; transform: translateY(0); }
          }

          /* Rendered Markdown inside a Tara assistant bubble. Tightens default
             block margins and scales headings down so rich text sits cleanly in
             the small chat bubble. */
          .tara-markdown > :first-child { margin-top: 0; }
          .tara-markdown > :last-child { margin-bottom: 0; }
          .tara-markdown p { margin: 0 0 0.5em; }
          .tara-markdown ul,
          .tara-markdown ol { margin: 0.25em 0 0.5em; padding-left: 1.25em; }
          .tara-markdown li { margin: 0.15em 0; }
          .tara-markdown h1,
          .tara-markdown h2,
          .tara-markdown h3,
          .tara-markdown h4,
          .tara-markdown h5,
          .tara-markdown h6 {
            font-size: 1em;
            font-weight: 600;
            margin: 0.5em 0 0.25em;
            line-height: 1.3;
          }
          .tara-markdown code {
            background: rgba(0, 0, 0, 0.06);
            padding: 0.05em 0.3em;
            border-radius: 4px;
            font-size: 0.85em;
          }
          .tara-markdown pre {
            background: rgba(0, 0, 0, 0.06);
            padding: 0.5em 0.7em;
            border-radius: 8px;
            overflow-x: auto;
            margin: 0.4em 0;
          }
          .tara-markdown pre code { background: none; padding: 0; }
          .tara-markdown a {
            color: #00897b;
            text-decoration: underline;
            word-break: break-word;
            cursor: pointer;
          }
          .tara-markdown blockquote {
            margin: 0.25em 0 0.5em;
            padding-left: 0.6em;
            border-left: 3px solid rgba(0, 0, 0, 0.12);
            color: #4b5563;
          }
          .tara-markdown table {
            display: block;
            width: max-content;
            max-width: 100%;
            overflow-x: auto;
            border-collapse: collapse;
            margin: 0.4em 0;
            font-size: 0.9em;
          }
          .tara-markdown th,
          .tara-markdown td {
            border: 1px solid rgba(0, 0, 0, 0.15);
            padding: 0.3em 0.5em;
            text-align: left;
            vertical-align: top;
          }
          .tara-markdown th { background: rgba(0, 0, 0, 0.04); font-weight: 600; }
        `}
      </style>
      {/* ── Header Controls: Close ── */}
      <div
        style={{
          position: "absolute",
          top: "14px",
          right: "14px",
          zIndex: 10,
          display: "flex",
          alignItems: "center",
          gap: "6px",
        }}
      >
        {/* Close Button */}
        {onClose && (
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "rgba(255, 255, 255, 0.6)",
              fontSize: "20px",
              cursor: "pointer",
              padding: "4px",
              lineHeight: 1,
              transition: "color 0.2s ease",
            }}
            onMouseEnter={(e) => (e.target.style.color = "#ffffff")}
            onMouseLeave={(e) =>
              (e.target.style.color = "rgba(255, 255, 255, 0.6)")
            }
          >
            ✕
          </button>
        )}
      </div>
      {/* Fixed Header Group — Horizontal layout */}
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: "14px",
          flexShrink: 0,
          width: "100%",
          marginBottom: "16px",
        }}
      >
        <div style={defaultStyles.avatarWrapper}>
          <img
            src={avatarSrc}
            alt="Chatbot avatar Tara"
            style={defaultStyles.avatar}
            onError={(e) => {
              e.target.src =
                "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHZpZXdCb3g9IjAgMCA4MCA4MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iNDAiIGN5PSI0MCIgcj0iNDAiIGZpbGw9IiNmZmZmZmYiLz4KPHN2ZyB4PSIyMCIgeT0iMjAiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiMzMzMiIHN0cm9rZS13aWR0aD0iMiI+CjxwYXRoIGQ9Im0yMCA5LTctNy03IDcgNy03IDctN3oiLz4KPHN2Zz4KPC9zdmc+";
            }}
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span style={defaultStyles.greeting}>{greeting}</span>
          <p style={defaultStyles.tagline}>{tagline}</p>
        </div>
      </div>

      {/* Rotating Prompts Section */}
      <div style={defaultStyles.prompts}>
        {isLoadingPrompts ? (
          <div
            style={{
              textAlign: "center",
              padding: "12px",
              color: "rgba(255,255,255,0.7)",
              fontSize: "14px",
            }}
          >
            Loading prompts...
          </div>
        ) : (
          prompts.length > 0 && (
            <button
              key={prompts[currentPromptIndex].id || currentPromptIndex}
              style={{
                ...defaultStyles.promptButton,
                opacity: isPromptFading ? 0 : 1,
                transform: isPromptFading ? "scale(0.98)" : "scale(1)",
                color: "#fff",
              }}
              onClick={() => handlePromptClick(prompts[currentPromptIndex])}
            >
              <img
                src={prompts[currentPromptIndex].icon}
                alt="prompt icon"
                width={16}
                height={16}
                style={{ opacity: 0.7 }}
              />{" "}
              {prompts[currentPromptIndex].text}
            </button>
          )
        )}
      </div>

      {/* Chat messages */}
      {hasMessages && (
        <div
          ref={chatContainerRef}
          onScroll={handleScroll}
          className="custom-scrollbar"
          style={{
            width: "100%",
            margin: "0 0 16px 0",
            flex: "1 1 auto", // Takes up available space
            minHeight: "150px", // Minimum height for message area
            overflowY: "auto",
            overflowX: "hidden",
            background: "rgba(255,255,255,0.07)",
            borderRadius: "16px",
            boxSizing: "border-box",
            // This prevents sharp corners during scroll clipping in some browsers
            maskImage: "-webkit-radial-gradient(white, black)",
            WebkitMaskImage: "-webkit-radial-gradient(white, black)",
            transform: "translateZ(0)",
          }}
        >
          <div
            style={{
              padding: "16px",
              display: "flex",
              flexDirection: "column",
              gap: "10px",
            }}
          >
            {messages.map((message) => (
              message.sender === "system" ? (
                <div
                  key={message.id}
                  style={{
                    background: "rgba(245, 158, 11, 0.15)",
                    border: "1px solid rgba(245, 158, 11, 0.4)",
                    borderRadius: "14px",
                    padding: "10px 14px",
                    fontSize: "13px",
                    lineHeight: "1.5",
                    color: "#fbbf24",
                    textAlign: "center",
                    animation: "systemMsgFadeIn 0.3s ease",
                  }}
                >
                  {message.text}
                </div>
              ) : (
                <div
                  key={message.id}
                  style={{
                    display: "flex",
                    justifyContent:
                      message.sender === "user" ? "flex-end" : "flex-start",
                  }}
                >
                  <div
                    style={{
                      background:
                        message.sender === "user" ? "#00bfae" : "#ffffff",
                      color: message.sender === "user" ? "#ffffff" : "#1e4a4a",
                      borderRadius: "16px",
                      borderBottomRightRadius:
                        message.sender === "user" ? "4px" : "16px",
                      borderBottomLeftRadius:
                        message.sender === "user" ? "16px" : "4px",
                      padding: "10px 14px",
                      maxWidth: "85%",
                      fontSize: "14px",
                      lineHeight: "1.4",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                      wordBreak: "break-word",
                      userSelect: "none",
                      WebkitUserSelect: "none",
                      msUserSelect: "none",
                      caretColor: "transparent",
                      cursor: "default",
                    }}
                  >
                    {message.sender === "ai" ? (
                      <MarkdownMessage
                        text={message.text}
                        className="tara-markdown"
                      />
                    ) : (
                      message.text
                    )}
                  </div>
                </div>
              )
            ))}
            {/* Typing indicator */}
            {isTyping && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-start",
                }}
              >
                <div
                  style={{
                    background: "#ffffff",
                    color: "#1e4a4a",
                    borderRadius: "16px",
                    borderBottomLeftRadius: "4px",
                    padding: "14px 16px",
                    maxWidth: "80%",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <div className="dot-typing">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} style={{ height: 1 }} />
          </div>
        </div>
      )}


      
      {/* Input section */}
      {errorMessage && (
        <div
          style={{
            width: '100%',
            marginBottom: '12px',
            padding: '12px 16px',
            borderRadius: '16px',
            background: 'rgba(248, 113, 113, 0.12)',
            border: '1px solid rgba(248, 113, 113, 0.3)',
            color: '#fee2e2',
            fontSize: '13px',
            lineHeight: 1.4,
          }}
        >
          {errorMessage}
        </div>
      )}
      <form
        onSubmit={handleChatSubmit}
        style={{ ...defaultStyles.inputWrapper, opacity: isLimitReached ? 0.5 : 1 }}
      >
        <input
          type="text"
          className="chatbot-input"
          placeholder={placeholder}
          value={chatInput}
          disabled={isLimitReached}
          onChange={(e) => {
            setChatInput(e.target.value);
            if (errorMessage) setErrorMessage('');
          }}
          maxLength={MAX_INPUT_LENGTH}
          style={{ 
            ...defaultStyles.input, 
            color: "#fff", 
            cursor: isLimitReached ? "not-allowed" : "text",
            opacity: isLimitReached ? 0.5 : 1
          }}
        />
        <button
          type="submit"
          style={{
            ...defaultStyles.sendButton,
            cursor: isLimitReached || !chatInput.trim() ? "not-allowed" : "pointer",
            opacity: isLimitReached || !chatInput.trim() ? 0.5 : 1,
          }}
          disabled={isLimitReached || !chatInput.trim()}
        >
          <img
            src={sendIconSrc}
            alt="send"
            width={16}
            height={16}
            style={{ opacity: isLimitReached || !chatInput.trim() ? 0.3 : 1 }}
          />
        </button>
      </form>

      {/* Footer Section - Fixed Capsule */}
      <div
        className="raise-ticket-footer"
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexShrink: 0,
          marginTop: "14px",
          paddingTop: "0",
          paddingBottom: "6px",
        }}
      >
        <div
          className="raise-ticket-footer-pill"
          style={{
            background:
              "linear-gradient(180deg, rgba(16, 93, 96, 0.92) 0%, rgba(10, 77, 82, 0.92) 100%)",
            border: "1px solid rgba(146, 222, 220, 0.55)",
            borderRadius: "999px",
            width: "100%",
            minHeight: "48px",
            padding: "11px 18px",
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
            whiteSpace: "nowrap",
            boxShadow:
              "inset 0 1px 0 rgba(196, 246, 244, 0.28), inset 0 -1px 0 rgba(0, 0, 0, 0.18), 0 0 0 1px rgba(103, 191, 190, 0.34)",
            transition: "all 0.2s ease",
            opacity: 1,
            cursor: "pointer",
          }}
          onClick={() => navigate("/suspire-helpdesk")}
        >
          <span
            style={{
              fontSize: "14px",
              color: "#BFD4D3",
              fontWeight: "500",
              lineHeight: 1,
              textShadow: "none",
            }}
          >
            Need more help?
          </span>
          <button
            type="button"
            style={{
              fontSize: "14px",
              color: "#00BFAE",
              textDecoration: "none",
              fontWeight: "700",
              textShadow: "none",
              cursor: "pointer",
              background: "transparent",
              border: "none",
              padding: 0,
              lineHeight: 1,
            }}
          >
            Raise a Ticket →
          </button>
        </div>
      </div>
      

    </div>
  );
}
